import {
  DEFAULT_NOTIFICATION_SOUNDS,
  isRingKind,
  MESSAGE_TONES,
  RING_VIBRATION_PATTERNS,
  RINGTONES,
  SOUND_KINDS,
  VIBRATION_PATTERNS,
  VIBRATIONS,
  type MessageTone,
  type NotificationSounds,
  type NotificationSoundsUpdate,
  type Ringtone,
  type SoundChoice,
  type SoundKind,
} from '@dijitalapp/shared';

// Alerts while the app is open but not in front: a system notification (the same look as a push), a soft
// sound, and the unread count in the tab title. With the tab closed, server push takes over (push/worker.ts);
// both use the same notification tag per chat, so one never stacks on top of the other.

export function canNotify(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/** The person is not looking at this chat right now (another tab, window, or another chat). */
export function lookingElsewhere(conversationId: string | null, activeConversationId: string | null): boolean {
  return document.visibilityState !== 'visible' || !document.hasFocus() || (conversationId !== null && conversationId !== activeConversationId);
}

const iconCache = new Map<string, { icon: string | null; at: number }>();
const ICON_TTL = 10 * 60_000;

/**
 * A photo of ours (group or person) made into a picture the notification can carry by itself: the system draws
 * notifications outside the page, without the session, so the photo travels inside it. Null when there is none.
 */
export async function photoIcon(url: string): Promise<string | null> {
  const hit = iconCache.get(url);
  if (hit && Date.now() - hit.at < ICON_TTL) return hit.icon;
  let icon: string | null = null;
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    const blob = res.status === 200 ? await res.blob() : null;
    if (blob && blob.type.startsWith('image/') && blob.size < 200_000) {
      icon = await new Promise<string | null>((done) => {
        const reader = new FileReader();
        reader.onload = () => done(typeof reader.result === 'string' ? reader.result : null);
        reader.onerror = () => done(null);
        reader.readAsDataURL(blob);
      });
    }
  } catch {
    /* the app's icon then */
  }
  if (iconCache.size > 100) iconCache.clear();
  iconCache.set(url, { icon, at: Date.now() });
  return icon;
}

/** Shown through the service worker when there is one (works on phones too), otherwise as a page notification. */
export async function showLocal(title: string, opts: { body: string; tag: string; url: string; requireInteraction?: boolean; icon?: string | null }): Promise<void> {
  if (!canNotify()) return;
  const options: NotificationOptions & { renotify?: boolean } = {
    body: opts.body,
    tag: opts.tag,
    renotify: true,
    // The group's or the writer's photo, like the server's push; the app's icon when there is none.
    icon: opts.icon ?? '/brand/icon-192.png',
    badge: '/brand/badge-96.png',
    // Marked as this page's own: a push for the same chat arriving right after does not show a second one.
    data: { url: opts.url, local: true, at: Date.now() },
    requireInteraction: opts.requireInteraction ?? false,
  };
  try {
    const reg = await navigator.serviceWorker?.getRegistration('/');
    if (reg) return await reg.showNotification(title, options);
  } catch {
    /* fall back to a page notification */
  }
  try {
    const n = new Notification(title, options);
    n.onclick = () => {
      window.focus();
      if (location.pathname !== opts.url) {
        history.pushState(null, '', opts.url);
        dispatchEvent(new PopStateEvent('popstate'));
      }
      n.close();
    };
  } catch {
    /* some browsers allow notifications only from a service worker */
  }
}

export async function closeLocal(tag: string): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration('/');
    for (const n of (await reg?.getNotifications({ tag })) ?? []) n.close();
  } catch {
    /* nothing to close */
  }
}

let audio: AudioContext | null = null;
/** A call has the microphone and speaker: no sounds of ours meanwhile (see holdSoundsForCall). */
let callHolds = 0;
function ctx(): AudioContext | null {
  if (callHolds > 0) return null;
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
    return audio;
  } catch {
    return null;
  }
}
// Browsers allow sound only after the person has interacted with the page once: unlock on the first tap.
function unlock() {
  if (callHolds > 0) return;
  ctx();
  window.removeEventListener('pointerdown', unlock);
  window.removeEventListener('keydown', unlock);
}
function armUnlock() {
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}
if (typeof window !== 'undefined') armUnlock();

type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };

/**
 * A call's media starts (on) or stops (off). On iPhones an AudioContext of ours that is running when WebRTC
 * takes the microphone keeps the audio session in its playback mode, which can leave the call silent or on the
 * earpiece at a whisper (WebKit, most often in home-screen apps): it is closed for the call and made again on the
 * next tap afterwards. Safari's audio session is also told it is a call (play and record) for the duration.
 */
export function holdSoundsForCall(on: boolean): void {
  callHolds = Math.max(0, callHolds + (on ? 1 : -1));
  const session = typeof navigator !== 'undefined' ? (navigator as AudioSessionNavigator).audioSession : undefined;
  try {
    if (session) session.type = callHolds > 0 ? 'play-and-record' : 'auto';
  } catch {
    /* older Safari: no audio session API */
  }
  if (callHolds > 0 && audio) {
    const closing = audio;
    audio = null;
    void closing.close().catch(() => undefined);
  }
  if (callHolds === 0 && typeof window !== 'undefined') armUnlock();
}

// ---------- sounds (Settings › Notifications › Sounds) ----------
// Every tone is synthesized here with Web Audio (nothing is downloaded): short tones for messages, ringtones that
// repeat for calls. The choices are the person's (GET /me/notifications `sounds`), kept on this device too so the
// first sound after a reload already follows them, and copied for the service worker (vibration of pushes).

const SOUNDS_KEY = 'dijitalapp-sounds';
function loadSounds(): NotificationSounds {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(SOUNDS_KEY) : null;
    if (raw) return mergeSounds(DEFAULT_NOTIFICATION_SOUNDS, JSON.parse(raw) as NotificationSoundsUpdate);
  } catch {
    /* defaults */
  }
  return DEFAULT_NOTIFICATION_SOUNDS;
}

/** Choices over others, member by member (what the server answers is always complete; this guards older copies). */
export function mergeSounds(base: NotificationSounds, patch: NotificationSoundsUpdate | null | undefined): NotificationSounds {
  if (!patch || typeof patch !== 'object') return base;
  const out: NotificationSounds = { ...base };
  if (typeof patch.enabled === 'boolean') out.enabled = patch.enabled;
  if (typeof patch.volume === 'number' && patch.volume >= 0 && patch.volume <= 100) out.volume = Math.round(patch.volume);
  for (const k of SOUND_KINDS) {
    const c = patch[k];
    if (!c || typeof c !== 'object') continue;
    const tones: readonly string[] = isRingKind(k) ? RINGTONES : MESSAGE_TONES;
    const tone = typeof c.tone === 'string' && tones.includes(c.tone) ? c.tone : base[k].tone;
    const vibration = typeof c.vibration === 'string' && (VIBRATIONS as readonly string[]).includes(c.vibration) ? c.vibration : base[k].vibration;
    (out as unknown as Record<SoundKind, SoundChoice>)[k] = { tone, vibration } as SoundChoice;
  }
  return out;
}

let sounds: NotificationSounds = loadSounds();
/** Texts the service worker needs for its buttons and encrypted previews (`preview`), in the person's language. */
let swTexts: Record<string, string | Record<string, string>> = {};

export function soundPrefs(): NotificationSounds {
  return sounds;
}

/** The person's choices (from the server, or just changed in Settings): used at once, here and by the worker. */
export function setSoundPrefs(next: NotificationSounds): void {
  sounds = next;
  try {
    localStorage.setItem(SOUNDS_KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
  void syncWorkerPrefs();
}

export function setWorkerTexts(texts: Record<string, string | Record<string, string>>): void {
  swTexts = texts;
  void syncWorkerPrefs();
}

/** The service worker cannot read this page's storage: it reads a copy in Cache Storage (sw-notify.js). */
async function syncWorkerPrefs(): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const cache = await caches.open('dijitalapp-prefs');
    await cache.put('/__sw-prefs', new Response(JSON.stringify({ sounds, texts: swTexts }), { headers: { 'content-type': 'application/json' } }));
  } catch {
    /* no Cache Storage (private mode): pushes vibrate the default way */
  }
}

interface ToneOpts {
  type?: OscillatorType;
  /** Glide to this frequency over the tone (a "pop" or a "bubble"). */
  to?: number;
}

function tone(c: AudioContext, freq: number, at: number, length: number, volume: number, opts: ToneOpts = {}) {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, at);
  if (opts.to) osc.frequency.exponentialRampToValueAtTime(opts.to, at + length * 0.6);
  // The person's volume: 80 (the default) is a little above the original level, 0 is silent.
  const v = Math.max(0.0002, volume * (sounds.volume / 100) * 1.5);
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(v, at + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + length + 0.05);
}

/** One message tone at time t. */
const MESSAGE_SYNTHS: Record<Exclude<MessageTone, 'none'>, (c: AudioContext, t: number) => void> = {
  // The original soft two-note sound.
  chime: (c, t) => {
    tone(c, 880, t, 0.18, 0.07);
    tone(c, 1318.5, t + 0.12, 0.28, 0.06);
  },
  pop: (c, t) => tone(c, 520, t, 0.12, 0.11, { to: 1150 }),
  ding: (c, t) => {
    tone(c, 1568, t, 0.7, 0.06);
    tone(c, 3136, t, 0.35, 0.015);
  },
  bubble: (c, t) => {
    tone(c, 380, t, 0.1, 0.08, { to: 900 });
    tone(c, 520, t + 0.11, 0.12, 0.07, { to: 1250 });
  },
  marimba: (c, t) => {
    tone(c, 784, t, 0.22, 0.1, { type: 'triangle' });
    tone(c, 1046.5, t + 0.1, 0.3, 0.09, { type: 'triangle' });
  },
};

/** One ring at time t; it repeats every RING_EVERY_MS. */
const RING_SYNTHS: Record<Exclude<Ringtone, 'none'>, (c: AudioContext, t: number) => void> = {
  // The original "ring-ring".
  classic: (c, t) => {
    for (const offset of [0, 0.45]) {
      tone(c, 523.25, t + offset, 0.35, 0.12);
      tone(c, 659.25, t + offset, 0.35, 0.08);
    }
  },
  pulse: (c, t) => {
    for (let i = 0; i < 4; i++) tone(c, 880, t + i * 0.22, 0.13, 0.08, { type: 'triangle' });
  },
  marimba: (c, t) => {
    [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5].forEach((f, i) => tone(c, f, t + i * 0.16, 0.3, 0.1, { type: 'triangle' }));
  },
  digital: (c, t) => {
    for (let i = 0; i < 8; i++) tone(c, i % 2 ? 960 : 1280, t + i * 0.08, 0.07, 0.035, { type: 'square' });
  },
  soft: (c, t) => {
    tone(c, 440, t, 0.9, 0.08);
    tone(c, 554.37, t + 0.25, 0.9, 0.06);
    tone(c, 659.25, t + 0.5, 1, 0.05);
  },
};
const RING_EVERY_MS = 2600;

function vibrate(pattern: number[] | 0) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* not on this device */
  }
}

let lastChime = 0;
/** A short sound for a new message, as chosen for direct or group chats (at most one per second and a half). */
export function chime(kind: 'message' | 'group' = 'message'): void {
  const now = Date.now();
  if (now - lastChime < 1500) return;
  lastChime = now;
  const choice = sounds[kind];
  // Phones (Android) vibrate as chosen, also when the sound is off.
  if (choice.vibration !== 'none') vibrate(VIBRATION_PATTERNS[choice.vibration]);
  if (!sounds.enabled || choice.tone === 'none') return;
  const c = ctx();
  if (!c || c.state !== 'running') return;
  MESSAGE_SYNTHS[choice.tone](c, c.currentTime);
}

/** Ringing for an incoming call until stopped (with vibration on phones), as chosen for this kind of call. */
export function ringtone(kind: 'call' | 'video' | 'groupCall' = 'call'): { stop: () => void } {
  const c = ctx();
  const choice = sounds[kind];
  let stopped = false;
  const ring = () => {
    if (stopped) return;
    if (c && c.state === 'running' && sounds.enabled && choice.tone !== 'none') RING_SYNTHS[choice.tone](c, c.currentTime);
    if (choice.vibration !== 'none') vibrate(RING_VIBRATION_PATTERNS[choice.vibration]);
  };
  ring();
  const timer = setInterval(ring, RING_EVERY_MS);
  return {
    stop: () => {
      stopped = true;
      clearInterval(timer);
      vibrate(0);
    },
  };
}

/** Settings: plays a choice once (a ring: one ring) at the given volume, even while sounds are off. */
export function previewSound(kind: SoundKind, choice: SoundChoice, volume: number): void {
  const c = ctx();
  const saved = sounds;
  sounds = { ...sounds, enabled: true, volume };
  try {
    if (c && choice.tone !== 'none') {
      if (c.state === 'suspended') void c.resume();
      if (isRingKind(kind)) RING_SYNTHS[choice.tone as Exclude<Ringtone, 'none'>]?.(c, c.currentTime + 0.02);
      else MESSAGE_SYNTHS[choice.tone as Exclude<MessageTone, 'none'>]?.(c, c.currentTime + 0.02);
    }
  } finally {
    sounds = saved;
  }
  if (choice.vibration !== 'none') vibrate((isRingKind(kind) ? RING_VIBRATION_PATTERNS : VIBRATION_PATTERNS)[choice.vibration]);
}

// ---------- tab title ----------
// The tab says what is waiting, in words: "(3) 2 new messages · 1 missed call — DijitalApp". Something that
// arrives while the app is in the background also blinks ("💬 Ayşe: new message") until the person looks.

const BASE_TITLE = typeof document !== 'undefined' ? document.title || 'DijitalApp' : 'DijitalApp';
let unread = 0;
let missed = 0;
let flashTimer: ReturnType<typeof setInterval> | null = null;
let flashText: string | null = null;
/** A ringing call blinks until it ends; other blinking stops when the person comes back. */
let persistent = false;
/** Texts in the person's language (set by the app). */
let texts = {
  unread: (n: number) => `${n} new ${n === 1 ? 'message' : 'messages'}`,
  missed: (n: number) => `${n} missed ${n === 1 ? 'call' : 'calls'}`,
};

export function setTitleTexts(next: typeof texts): void {
  texts = next;
  render(false);
}

function steady(): string {
  const parts = [missed > 0 ? `📞 ${texts.missed(missed)}` : null, unread > 0 ? texts.unread(unread) : null].filter(Boolean);
  const total = unread + missed;
  return parts.length ? `(${total > 99 ? '99+' : total}) ${parts.join(' · ')} — ${BASE_TITLE}` : BASE_TITLE;
}

function render(on: boolean) {
  if (typeof document === 'undefined') return;
  document.title = flashText && on ? flashText : steady();
}

function stopFlash() {
  if (flashTimer) clearInterval(flashTimer);
  flashTimer = null;
  flashText = null;
  persistent = false;
  render(false);
}

/** Unread messages (not muted chats), shown in words in the tab title. */
export function setTitleUnread(count: number): void {
  unread = count;
  if (!flashTimer) render(false);
  // Installed app: the same number on the icon (Badging API; a no-op where unsupported).
  try {
    if ('setAppBadge' in navigator) void (count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge())?.catch(() => undefined);
  } catch {
    /* unsupported */
  }
}

/** Alternates the tab title with a message until stopped (an incoming call). */
export function flashTitle(text: string, untilStopped = true): () => void {
  if (flashTimer) clearInterval(flashTimer);
  flashText = text;
  persistent = untilStopped;
  let on = true;
  render(on);
  flashTimer = setInterval(() => {
    on = !on;
    render(on);
  }, 1000);
  return stopFlash;
}

const looking = () => document.visibilityState === 'visible' && document.hasFocus();

/** Something new while the app is in the background: the title blinks with it until the person looks. */
export function attention(text: string): void {
  if (looking() || persistent) return;
  flashTitle(text, false);
}

/** A call I did not answer: counted in the title until I come back to the app. */
export function addMissedCall(text: string): void {
  missed += 1;
  if (looking()) {
    // In front: no need to keep a reminder in the title.
    missed = 0;
    return;
  }
  flashTitle(text, false);
}

if (typeof window !== 'undefined') {
  const back = () => {
    if (!looking()) return;
    missed = 0;
    // A ringing call keeps its own blinking (it stops when the call is answered or ends).
    if (!persistent) stopFlash();
    else render(false);
  };
  window.addEventListener('focus', back);
  document.addEventListener('visibilitychange', back);
}
